import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { IApprovalTemplate } from "../../../types/global";
import { IPurchaseOrder } from "../../../types/Purchase/purchaseOrder";
import {
  PurchaseOrderCreateInput,
  PurchaseOrderFindUniqueQuery,
  PurchaseOrderUpdateInput,
  usePurchaseOrderCreateMutation,
  usePurchaseOrderFindUniqueQuery,
  usePurchaseOrderUpdateMutation,
} from "../../../generated/purchase";
import {
  ActivityType,
  ActivityLogDocumentType,
} from "../../../generated/general";

import { useDisable } from "../../../hooks/use-disable";
import { useActivityLog } from "../../../hooks/use-activity-log";
import { usePurchaseOption } from "../../../hooks/Purchase/use-purchase-option";
import { useStateContext } from "../../../contexts/auth-context";

import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";

import { Stack } from "@mui/material";
import LoadingUI from "../../../components/UI/LoadingUI";
import PurchaseFooter from "../../../components/Form/Purchase/Footer";
import VendorInfo from "../../../components/Form/Purchase/VendorInfo";
import BottomNavbar from "../../../components/UI/Navbar/BottomNavbar";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import AddressInfo from "../../../components/Form/Purchase/VendorInfo/AddressInfo";
import PurchaseOrderHeader from "../../../components/Form/Purchase/Order/Header";
import PurchaseOrderItemList from "../../../components/Table/Purchase/ItemList/PurchaseOrderItemList";

import {
  purchaseOrderQueryFormatter,
  purchaseOrderCreatePayloadFormatter,
  purchaseOrderUpdatePayloadFormatter,
} from "../../../utils/Formatter/Purchase/PurchaseOrder";
import { errorMessageFormatter } from "../../../utils/Global";
import { IExternalProps } from "../../../types/Sales";

const DocumentInfoTab = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();
  const documentType = "purchase_order";
  const { enqueueSnackbar } = useSnackbar();
  const [disabled, setDisabled] = useDisable();
  const { createActivityLog } = useActivityLog();
  const [approvalStep, setApprovalStep] = useState<number>(0);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [isHaveApproval, setIsHaveApproval] = useState<boolean>(false);
  const [approverList, setApproverList] = useState<IApprovalTemplate[]>([]);
  const { isLoading: isOptionLoading, approvalTemplates } =
    usePurchaseOption(documentType);

  const isApproval = approvalTemplates && approvalTemplates.length > 0;

  const {
    state: { authUser, permissions },
  } = useStateContext();

  const graphQLClient = createGraphQLClientWithMiddleware("purchase");

  const {
    getValues,
    reset,
    handleSubmit,
    watch,
    formState: { dirtyFields },
  } = useFormContext<IPurchaseOrder>();

  const status = watch("aggrid_status");
  const [netAmount, setNetAmount] = useState<number | null | undefined>(0);

  const { data, isLoading, isSuccess, refetch } =
    usePurchaseOrderFindUniqueQuery<PurchaseOrderFindUniqueQuery>(
      graphQLClient,
      {
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      },
      {
        enabled: !!id,
        cacheTime: 0,
      }
    );

  useEffect(() => {
    setIsLoadingData(true);
    if (isSuccess) {
      const { PurchaseOrderFindUnique } = data;
      setNetAmount(PurchaseOrderFindUnique.net_amount);
      const getPurchaseOrderData = async () => {
        const formattedPurchaseOrder = await purchaseOrderQueryFormatter(
          PurchaseOrderFindUnique
        );
        reset(formattedPurchaseOrder);
        setIsLoadingData(false);
      };
      getPurchaseOrderData();
    }
  }, [data, isSuccess, reset]);

  useEffect(() => {
    if (isSuccess) {
      const { PurchaseOrderFindUnique } = data;
      const currentStep = PurchaseOrderFindUnique?.approval_step as number;
      setApprovalStep(currentStep);
      if (PurchaseOrderFindUnique?.main_status === "wait_approve") {
        setApproverList(approvalTemplates as IApprovalTemplate[]);
      } else {
        const allApproval = PurchaseOrderFindUnique?.approver_list || [];
        setApproverList(allApproval);
      }

      const currentReviewerList =
        approvalTemplates.find(
          (template) => template?.step_number === currentStep
        )?.approver_list || [];

      const isHaveApproval = currentReviewerList.some(
        (reviewer) => reviewer.id === authUser?.id
      );

      setIsHaveApproval(isHaveApproval);
    }
  }, [approvalTemplates, authUser?.id, data, isSuccess]);

  useEffect(() => {
    if (
      id &&
      ((status !== "draft" && status !== "not_approved") ||
        !permissions?.purchase_order?.update)
    ) {
      setDisabled(true);
    }
    return () => {
      setDisabled(false);
    };
  }, [id, permissions?.purchase_order?.update, setDisabled, status]);

  useEffect(() => {
    if (state) {
      const { copied_unique_id, copied_id, ...otherState } = state;
      reset({
        ...otherState,
        created_date: dayjs(),
        issue_date: dayjs(),
        expected_date: dayjs(),
        within_date: dayjs(),
      });
    }
  }, [reset, state]);

  const { mutateAsync: create, isLoading: isCreating } =
    usePurchaseOrderCreateMutation<Error>(graphQLClient);

  const { mutateAsync: update, isLoading: isUpdating } =
    usePurchaseOrderUpdateMutation<Error>(graphQLClient);

  const draftHandler = async (data: IPurchaseOrder) => {
    if (!id) {
      try {
        const formatData = purchaseOrderCreatePayloadFormatter(data, "draft");
        const { PurchaseOrderCreate } = await create({
          input: formatData as PurchaseOrderCreateInput,
        });
        enqueueSnackbar(`${t("button.save_draft")}สำเร็จ`, {
          variant: "success",
        });
        navigate(`/purchase/order/${PurchaseOrderCreate.id}`);
        if (state && state.copied_id) {
          await createActivityLog({
            activity_type: ActivityType.Copy,
            document_type: ActivityLogDocumentType.PurchaseOrder,
            reference_id: PurchaseOrderCreate?.id,
            activity_detail: {
              copied_from: {
                id: state.copied_id,
                unique_id: state.copied_unique_id,
              },
              copied_to: {
                id: PurchaseOrderCreate.id,
                unique_id: PurchaseOrderCreate.unique_id,
              },
            },
          });
        }
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.PurchaseOrder,
          reference_id: PurchaseOrderCreate?.id,
          activity_detail: {
            secondary_operation: ActivityType.Create,
            curr_status: "draft",
            updated_fields: {
              net_amount: PurchaseOrderCreate.net_amount,
            },
          },
        });
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(formatError || `${t("button.save_draft")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    } else {
      try {
        const formatData = await purchaseOrderUpdatePayloadFormatter(
          data,
          "draft"
        );
        const { PurchaseOrderUpdate } = await update({
          uniqueInput: {
            id: id ? parseInt(id) : undefined,
          },
          input: formatData as PurchaseOrderUpdateInput,
        });
        enqueueSnackbar(`${t("button.save_draft")}สำเร็จ`, {
          variant: "success",
        });

        await refetch();

        await createActivityLog({
          activity_type: ActivityType.Edit,
          document_type: ActivityLogDocumentType.PurchaseOrder,
          reference_id: PurchaseOrderUpdate?.id,
          activity_detail: {
            secondary_operation: ActivityType.Edit,
            updated_fields: {
              net_amount:
                netAmount?.toFixed(2) ===
                PurchaseOrderUpdate.net_amount?.toFixed(2)
                  ? undefined
                  : PurchaseOrderUpdate.net_amount,
            },
          },
        });
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(formatError || `${t("button.save_draft")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    }
  };

  const waitApproveHandler = async (data: IPurchaseOrder) => {
    if (!id) {
      try {
        const formatData = purchaseOrderCreatePayloadFormatter(
          data,
          "wait_approve",
          isApproval ? 1 : 0
        );
        const { PurchaseOrderCreate } = await create({
          input: formatData as PurchaseOrderCreateInput,
        });
        enqueueSnackbar(`${t("button.send") + t("button.approve")}สำเร็จ`, {
          variant: "success",
        });
        navigate(`/purchase/order/${PurchaseOrderCreate.id}`);
        if (state && state.copied_id) {
          await createActivityLog({
            activity_type: ActivityType.Copy,
            document_type: ActivityLogDocumentType.PurchaseOrder,
            reference_id: PurchaseOrderCreate?.id,
            activity_detail: {
              copied_from: {
                id: state.copied_id,
                unique_id: state.copied_unique_id,
              },
              copied_to: {
                id: PurchaseOrderCreate.id,
                unique_id: PurchaseOrderCreate.unique_id,
              },
            },
          });
        }
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.PurchaseOrder,
          reference_id: PurchaseOrderCreate?.id,
          activity_detail: {
            secondary_operation: ActivityType.Create,
            curr_status: "wait_approve",
            updated_fields: {
              net_amount: PurchaseOrderCreate.net_amount,
            },
          },
        });
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(
          formatError || `${t("button.send") + t("button.approve")}ไม่สำเร็จ`,
          {
            variant: "error",
          }
        );
      }
    } else {
      try {
        const formatData = await purchaseOrderUpdatePayloadFormatter(
          data,
          "wait_approve",
          isApproval ? 1 : 0
        );
        const { PurchaseOrderUpdate } = await update({
          uniqueInput: {
            id: id ? parseInt(id) : undefined,
          },
          input: formatData as PurchaseOrderUpdateInput,
        });
        await refetch();
        const formattedDirtyFields = Object.keys(dirtyFields);
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.PurchaseOrder,
          reference_id: PurchaseOrderUpdate?.id,
          activity_detail: {
            secondary_operation:
              formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
            prev_status: data.aggrid_status,
            curr_status: "wait_approve",
            updated_fields: {
              net_amount:
                netAmount?.toFixed(2) ===
                PurchaseOrderUpdate.net_amount?.toFixed(2)
                  ? undefined
                  : PurchaseOrderUpdate.net_amount,
            },
          },
        });
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(
          formatError || `${t("button.send") + t("button.approve")}ไม่สำเร็จ`,
          {
            variant: "error",
          }
        );
      }
    }
  };

  const approveHandler = async (data: IPurchaseOrder) => {
    if (!id) {
      //guarantee no approval
      try {
        const reviewed_by = {
          user_id: authUser?.id,
          first_name: authUser?.first_name || "",
          last_name: authUser?.last_name || "",
          reviewed_date: dayjs(),
        };

        const formatData = purchaseOrderCreatePayloadFormatter(
          data,
          "approved",
          undefined,
          reviewed_by
        );

        const { PurchaseOrderCreate } = await create({
          input: formatData as PurchaseOrderCreateInput,
        });
        enqueueSnackbar(`${t("button.approve")}สำเร็จ`, {
          variant: "success",
        });
        navigate(`/purchase/order/${PurchaseOrderCreate.id}`);
        if (state && state.copied_id) {
          await createActivityLog({
            activity_type: ActivityType.Copy,
            document_type: ActivityLogDocumentType.PurchaseOrder,
            reference_id: PurchaseOrderCreate?.id,
            activity_detail: {
              copied_from: {
                id: state.copied_id,
                unique_id: state.copied_unique_id,
              },
              copied_to: {
                id: PurchaseOrderCreate.id,
                unique_id: PurchaseOrderCreate.unique_id,
              },
            },
          });
        }
        // const reference_document_list = getValues("reference_document_list");
        // if (reference_document_list && reference_document_list.length > 0) {
        //   await createActivityLog({
        //     activity_type: ActivityType.StatusChange,
        //     document_type: ActivityLogDocumentType.PurchaseOrder,
        //     reference_id: reference_document_list[0].document_id,
        //     activity_detail: {
        //       curr_status: "finished",
        //     },
        //   });
        // }
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.PurchaseOrder,
          reference_id: PurchaseOrderCreate?.id,
          activity_detail: {
            secondary_operation: ActivityType.Create,
            curr_status: "approved",
          },
        });
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(formatError || `${t("button.approve")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    } else {
      try {
        let changingStatus = "wait_approve";
        let currentApprovalList: IApprovalTemplate[] = [];
        if (approvalStep && approvalStep > approvalTemplates.length - 1) {
          changingStatus = "approved";
          currentApprovalList = approvalTemplates as IApprovalTemplate[];
        }

        if (!isApproval) {
          changingStatus = "approved";
        }

        let currentApprovalStep = (approvalStep || 0) + 1;

        if (data.aggrid_status === "not_approved") {
          currentApprovalStep = approvalStep || 0;
        }
        const reviewed_by = {
          user_id: authUser?.id,
          first_name: authUser?.first_name || "",
          last_name: authUser?.last_name || "",
          reviewed_date: dayjs(),
        };

        const formatData = await purchaseOrderUpdatePayloadFormatter(
          data,
          changingStatus,
          currentApprovalStep,
          currentApprovalList,
          false,
          reviewed_by
        );
        const { PurchaseOrderUpdate } = await update({
          uniqueInput: {
            id: id ? parseInt(id) : undefined,
          },
          input: formatData as PurchaseOrderUpdateInput,
        });
        enqueueSnackbar(`${t("button.approve")}สำเร็จ`, {
          variant: "success",
        });

        await refetch();

        if (isApproval) {
          if (changingStatus === "approved") {
            await createActivityLog({
              activity_type: ActivityType.StatusChange,
              document_type: ActivityLogDocumentType.PurchaseOrder,
              reference_id: PurchaseOrderUpdate?.id,
              activity_detail: {
                secondary_operation: ActivityType.Approve,
                message: [`ลำดับที่ ${approvalStep}`],
                prev_status: "wait_approve",
                curr_status: "approved",
              },
            });
          } else {
            await createActivityLog({
              activity_type: ActivityType.Approve,
              document_type: ActivityLogDocumentType.PurchaseOrder,
              reference_id: PurchaseOrderUpdate?.id,
              activity_detail: {
                message: [`ลำดับที่ ${approvalStep}`],
              },
            });
          }
        } else {
          const formattedDirtyFields = Object.keys(dirtyFields);
          await createActivityLog({
            activity_type: ActivityType.StatusChange,
            document_type: ActivityLogDocumentType.PurchaseOrder,
            reference_id: PurchaseOrderUpdate?.id,
            activity_detail: {
              secondary_operation:
                formattedDirtyFields?.length > 0
                  ? ActivityType.Edit
                  : undefined,
              prev_status: data.aggrid_status,
              curr_status: "approved",
              updated_fields:
                formattedDirtyFields?.length > 0
                  ? formattedDirtyFields
                  : undefined,
            },
          });
        }
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(formatError || `${t("button.approve")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    }
  };

  const notApproveHandler = async (data: IPurchaseOrder) => {
    try {
      const flag_status =
        data?.flag_status && data.flag_status.length > 0
          ? data.flag_status.includes("not_approved")
            ? [...data.flag_status]
            : [...data.flag_status, "not_approved"]
          : ["not_approved"];

      const { PurchaseOrderUpdate } = await update({
        input: {
          branch_id: data.branch_id,
          flag_status: flag_status,
        } as PurchaseOrderUpdateInput,
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      });

      await refetch();

      const formattedDirtyFields = Object.keys(dirtyFields);

      await createActivityLog({
        activity_type: ActivityType.StatusChange,
        document_type: ActivityLogDocumentType.PurchaseOrder,
        reference_id: PurchaseOrderUpdate?.id,
        activity_detail: {
          secondary_operation:
            formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
          prev_status: data.aggrid_status,
          curr_status: "not_approved",
          updated_fields:
            formattedDirtyFields?.length > 0 ? formattedDirtyFields : undefined,
        },
      });

      enqueueSnackbar(`${t("button.not_approve")}สำเร็จ`, {
        variant: "success",
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("button.not_approve")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const editHandler = async (data: IPurchaseOrder, isNotApprove?: boolean) => {
    try {
      const formatData = await purchaseOrderUpdatePayloadFormatter(
        data,
        data?.main_status || "",
        approvalStep,
        approverList,
        isNotApprove
      );

      const { PurchaseOrderUpdate } = await update({
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
        input: formatData as PurchaseOrderUpdateInput,
      });

      enqueueSnackbar(`${t("sentence.edit")}สำเร็จ`, {
        variant: "success",
      });

      await refetch();

      const formattedDirtyFields = Object.keys(dirtyFields);

      await createActivityLog({
        activity_type: ActivityType.Edit,
        document_type: ActivityLogDocumentType.PurchaseOrder,
        reference_id: PurchaseOrderUpdate?.id,
        activity_detail: {
          secondary_operation:
            formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
          updated_fields: {
            net_amount:
              netAmount?.toFixed(2) ===
              PurchaseOrderUpdate.net_amount?.toFixed(2)
                ? undefined
                : PurchaseOrderUpdate.net_amount,
          },
        },
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("sentence.edit")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const externalReferenceHandler = async (data: IExternalProps) => {
    try {
      const formData = getValues();

      const { PurchaseOrderUpdate } = await update({
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
        input: {
          unique_id: formData.unique_id,
          branch_id: formData.branch_id || 0,
          reference_document_id: data.external_reference_no,
        },
      });

      enqueueSnackbar(`${t("sentence.edit")}สำเร็จ`, {
        variant: "success",
      });

      await refetch();

      const formattedDirtyFields = Object.keys(dirtyFields);

      await createActivityLog({
        activity_type: ActivityType.Edit,
        document_type: ActivityLogDocumentType.PurchaseOrder,
        reference_id: PurchaseOrderUpdate?.id,
        activity_detail: {
          secondary_operation:
            formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
          updated_fields: {
            net_amount:
              netAmount?.toFixed(2) ===
              PurchaseOrderUpdate.net_amount?.toFixed(2)
                ? undefined
                : PurchaseOrderUpdate.net_amount,
          },
        },
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("sentence.edit")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const cancelHandler = async () => {
    try {
      const data = getValues();
      const flag_status =
        data?.flag_status && data.flag_status.length > 0
          ? data.flag_status.includes("cancelled")
            ? [...data.flag_status]
            : [...data.flag_status, "cancelled"]
          : ["cancelled"];

      const formatData = await purchaseOrderUpdatePayloadFormatter(
        data,
        data?.main_status || "",
        approvalStep,
        approverList
      );

      const { PurchaseOrderUpdate } = await update({
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
        input: {
          reference_document_list: data.reference_document_list,
          item_list: formatData.item_list,
          branch_id: data.branch_id,
          flag_status: flag_status,
        } as PurchaseOrderUpdateInput,
      });

      enqueueSnackbar(`${t("button.cancel")}สำเร็จ`, {
        variant: "success",
      });

      await refetch();

      const formattedDirtyFields = Object.keys(dirtyFields);
      await createActivityLog({
        activity_type: ActivityType.StatusChange,
        document_type: ActivityLogDocumentType.PurchaseOrder,
        reference_id: PurchaseOrderUpdate?.id,
        activity_detail: {
          secondary_operation:
            formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
          prev_status: data.aggrid_status,
          curr_status: "cancelled",
          updated_fields:
            formattedDirtyFields?.length > 0 ? formattedDirtyFields : undefined,
        },
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("button.cancel")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const cancelLeftReceiveQty = async () => {
    try {
      const data = getValues();

      const formatData = await purchaseOrderUpdatePayloadFormatter(
        data,
        data?.main_status || "",
        approvalStep,
        approverList
      );

      const { PurchaseOrderUpdate } = await update({
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
        input: {
          reference_document_list: data.reference_document_list,
          item_list: formatData.item_list,
          branch_id: data.branch_id,
          aggrid_status: "fully_imported",
        } as PurchaseOrderUpdateInput,
      });

      enqueueSnackbar(t("purchase.order.cancel_left_qty.success"), {
        variant: "success",
      });

      await refetch();

      await createActivityLog({
        activity_type: ActivityType.StatusChange,
        document_type: ActivityLogDocumentType.PurchaseOrder,
        reference_id: PurchaseOrderUpdate?.id,
        activity_detail: {
          prev_status: data.aggrid_status,
          curr_status: "fully_imported",
        },
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(t("purchase.order.cancel_left_qty.fail"), {
        variant: "error",
      });
    }
  };

  const editClickHandler = () => {
    setDisabled(false);
  };

  const cancelEditHandler = () => {
    setDisabled(true);
    reset();
  };

  const submitErrorHandler = (err: any) => {
    if (err?.item_list?.message === "กรุณาเพิ่มสินค้า") {
      enqueueSnackbar(err.item_list.message, {
        variant: "error",
      });
    }
  };

  const renderButton = (status: string | undefined) => {
    switch (status) {
      case "draft":
        return (
          <>
            <CustomizedButton
              variant="outlined"
              title={t("button.save_draft")}
              disabled={isCreating || isUpdating}
              onClick={handleSubmit(draftHandler, submitErrorHandler)}
            />
            {isApproval ? (
              <CustomizedButton
                title={t("button.send")}
                variant="contained"
                disabled={isCreating || isUpdating}
                onClick={handleSubmit(waitApproveHandler, submitErrorHandler)}
              />
            ) : (
              <CustomizedButton
                title={t("button.approve")}
                variant="contained"
                disabled={isCreating || isUpdating}
                onClick={handleSubmit(approveHandler, submitErrorHandler)}
              />
            )}
          </>
        );
      case "wait_approve":
        if (!disabled) {
          return (
            <>
              <CustomizedButton
                variant="outlined"
                title={t("button.cancel")}
                onClick={cancelEditHandler}
                disabled={isUpdating}
              />
              <CustomizedButton
                title={t("button.save")}
                variant="contained"
                onClick={() => {
                  handleSubmit(
                    (data) => editHandler(data),
                    submitErrorHandler
                  )();
                  setDisabled(true);
                }}
                disabled={isUpdating}
              />
            </>
          );
        } else {
          if (isHaveApproval && permissions?.purchase_order.update) {
            return (
              <>
                <CustomizedButton
                  variant="outlined"
                  title={t("button.not_approve")}
                  disabled={isUpdating}
                  onClick={handleSubmit(notApproveHandler, submitErrorHandler)}
                />
                <CustomizedButton
                  title={t("button.approve")}
                  variant="contained"
                  onClick={handleSubmit(approveHandler, submitErrorHandler)}
                  disabled={isUpdating}
                />
              </>
            );
          } else {
            return;
          }
        }
      case "not_approved":
        return (
          <>
            <CustomizedButton
              variant="outlined"
              title={t("button.save")}
              disabled={isUpdating}
              onClick={handleSubmit(
                (data) => editHandler(data, true),
                submitErrorHandler
              )}
            />
            <CustomizedButton
              title={t("button.send")}
              variant="contained"
              onClick={handleSubmit(waitApproveHandler, submitErrorHandler)}
              disabled={isUpdating}
            />
          </>
        );
      case "accepted":
      case "cancelled":
      case "approved":
      case "finished":
        if (!disabled) {
          return (
            <>
              <CustomizedButton
                variant="outlined"
                title={t("button.cancel")}
                disabled={isUpdating}
                onClick={cancelEditHandler}
              />
              <CustomizedButton
                title={t("button.save")}
                variant="contained"
                onClick={() => {
                  handleSubmit(
                    (data) => editHandler(data),
                    submitErrorHandler
                  )();
                  setDisabled(true);
                }}
                disabled={isUpdating}
              />
            </>
          );
        }
        return;
      default:
        return (
          <>
            <CustomizedButton
              variant="outlined"
              title={t("button.save_draft")}
              disabled={isCreating || isUpdating}
              onClick={handleSubmit(draftHandler, submitErrorHandler)}
            />
            {isApproval ? (
              <CustomizedButton
                title={t("button.send")}
                variant="contained"
                disabled={isCreating || isUpdating}
                onClick={handleSubmit(waitApproveHandler, submitErrorHandler)}
              />
            ) : (
              <CustomizedButton
                title={t("button.approve")}
                variant="contained"
                disabled={isCreating || isUpdating}
                onClick={handleSubmit(approveHandler, submitErrorHandler)}
              />
            )}
          </>
        );
    }
  };

  if (isOptionLoading || (id && (isLoadingData || isLoading))) {
    return <LoadingUI />;
  }

  return (
    <>
      <PurchaseOrderHeader
        approverList={approverList}
        approvalStep={approvalStep}
        editClickHandler={editClickHandler}
        cancelHandler={cancelHandler}
        externalReferenceHandler={externalReferenceHandler}
        cancelLeftReceiveQty={cancelLeftReceiveQty}
      />
      <VendorInfo />
      <AddressInfo />
      <PurchaseOrderItemList documentType={documentType} />
      <PurchaseFooter documentType={documentType} />
      <BottomNavbar>
        {(!disabled || status === "wait_approve") && (
          <Stack direction="row" spacing={1} alignItems="center">
            {renderButton(status)}
          </Stack>
        )}
      </BottomNavbar>
    </>
  );
};

export default DocumentInfoTab;
